package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.content
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.shifted
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.DisplayStyle

enum class LButtonVariant {
    PRIMARY,
    SECONDARY,
    GHOST,
    DANGER,
}

/**
 * The pointer-target floor, from the pen's product-components frame.
 *
 * Stated here rather than inlined because two things depend on it agreeing: this file's hit-area
 * overlay, and `scripts/check-control-geometry.mjs`, which measures rendered controls against the
 * same number. If they ever disagree the check goes quiet rather than wrong, which is worse.
 *
 * Derived from [ControlHeight.MD] rather than its own literal — the two are the same number for
 * the same reason ([ControlHeight] doc), and a control built at [ControlHeight.MD] now clears this
 * floor by construction instead of by coincidence.
 */
val HIT_AREA_FLOOR_PX = ControlHeight.MD.value.toInt()

enum class LButtonSize(
    val height: Int,
    val horizontalPadding: Int,
    // A type-scale step, not a raw rem double: 0.8125 / 0.875 / 1.0rem were exactly the kind of
    // number the scale exists to forbid, and every button in the product was off it as a result.
    val fontSize: CSSSizeValue<CSSUnit.px>,
    /**
     * A rung of [IconSize], not a bare `Int`.
     *
     * The three numbers never changed — 16, 18 and 20 are [IconSize.MD], [IconSize.LG] and
     * [IconSize.XL] — but spelling them as integers meant every use had to write `.px` twice, once
     * for a width and once for a height, which is the exact pair [Modifier.iconSize] exists to keep
     * from disagreeing. It is also what let the button's icon scale sit outside the scale the rest
     * of the design system sizes glyphs against, so a button icon could drift off it without
     * anything saying so.
     */
    val iconSize: CSSSizeValue<CSSUnit.px>,
) {
    SM(32, 12, TypeScale.Small, IconSize.MD),
    MD(40, 16, TypeScale.BodySmall, IconSize.LG),
    LG(48, 20, TypeScale.Body, IconSize.XL),
}

private val LButtonBackgroundVar by StyleVariable<CSSColorValue>()
private val LButtonHoverBackgroundVar by StyleVariable<CSSColorValue>()
private val LButtonForegroundVar by StyleVariable<CSSColorValue>()
private val LButtonBorderVar by StyleVariable<CSSColorValue>()

/**
 * The focus ring's colour, as a variable rather than a fixed token.
 *
 * The ring used to be `palette.accent` unconditionally, which is tuned for paper. On an ink band it
 * measured 2.70:1 against the ground in Day and 2.95:1 in Night — below the 3:1 that WCAG 2.4.11
 * requires of a focus indicator — so a keyboard visitor reaching the landing page's last and most
 * important control got an indicator they could barely see. The button's *label* was never the
 * problem (`accentContrast` on `accent` is 6.93:1 in Day), which is why this is a ring fix and not
 * a repaint of the button.
 */
private val LButtonFocusRingVar by StyleVariable<CSSColorValue>()

val LButtonStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "8px")
            }
            .backgroundColor(LButtonBackgroundVar.value())
            .color(LButtonForegroundVar.value())
            .border(1.px, LineStyle.Solid, LButtonBorderVar.value())
            .borderRadius(Radius.Control)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
            // The containing block for the loading spinner, which is taken out of flow so that
            // working does not change the button's size. See the `[data-busy-label]` rule below.
            .position(Position.Relative)
            .interactiveTransition()
    }
    /**
     * Gives a short button the pointer target the pen's floor requires, without changing its paint.
     *
     * **The pen states the rule numerically and names this exact failure**: "Every control is 40
     * tall. That is the hit-area floor, not a style preference — a 34px pill and a 36px nav item
     * both read fine and both fail it." [LButtonSize.SM] is 32, so every small button was eight
     * pixels short of a rule the design system calls non-negotiable — twelve of them on `/app` alone,
     * measured by `scripts/check-control-geometry.mjs`.
     *
     * Small controls retain their smaller type and icons. [minimumControlHitArea] additionally
     * reserves the 40px floor in layout, so clipping ancestors cannot trim this extended target.
     * The overlay is retained for callers sharing the base style outside [lButtonSurface].
     *
     * Width stays 100%: a text button is already wider than 40 — the narrowest measured was 59.8 —
     * so only the vertical axis was ever short.
     *
     * **Written into `LButtonStyle` rather than as its own `CssStyle`, and that is deliberate.** A
     * separate style has to be registered by name in every test harness that renders a button, and
     * the first version of this fix was exactly that: it failed eight editor tests with "declared at
     * the top-level of your file", an error that points at the component and not at the hand-written
     * registration list it is actually about. A rule inside a style that is already registered
     * everywhere cannot have that problem.
     */
    cssRule("[data-hit-extend]::after") {
        Modifier
            .content("")
            .position(Position.Absolute)
            .styleModifier {
                property("top", "50%")
                property("left", "0")
                property("width", "100%")
                property("height", "${HIT_AREA_FLOOR_PX}px")
                property("transform", "translateY(-50%)")
            }
    }
    cssRule("[data-hit-extend]") {
        Modifier.position(Position.Relative)
    }
    cssRule(":not(:disabled):hover") {
        Modifier.backgroundColor(LButtonHoverBackgroundVar.value())
    }
    // Compression on press. See Motion.PressScale for why it is this number and not a smaller one.
    cssRule(":not(:disabled):active:not(:focus-visible)") {
        Modifier
            .pressScale()
            .pressTransition()
    }
    focusVisible {
        Modifier
            // Falls back to the page accent so a button that never sets the variable — every button
            // on paper, which is nearly all of them — keeps exactly the ring it had.
            .outline(2.px, LineStyle.Solid, LButtonFocusRingVar.value(colorMode.toSitePalette().accent))
            .styleModifier { property("outline-offset", "2px") }
    }
    // Real unavailability: the button drops from the tab order and cannot be clicked. Styled from
    // `[data-disabled="true"]`, not the native `:disabled` pseudo-class this used to be — the paint
    // now comes from the one shared `disabledSurface()` every control in the design system uses,
    // rather than a `0.5` this component alone was carrying.
    cssRule("[data-disabled=\"true\"]") {
        Modifier
            .disabledSurface()
            .styleModifier { property("transform", "none") }
    }
    // `loading`'s pseudo-disabled, kept a distinct concept from the rule above: `aria-disabled`
    // communicates unavailability to assistive technology and to the eye without the native
    // `disabled` attribute, because a truly disabled element leaves the tab order and blurs focus —
    // the one moment the editor's save loop cannot afford, since it drives the next key from the
    // button that was just pressed. Same paint as real disabled (`disabledSurface()`); different
    // attribute, because the two are not the same fact about the button.
    cssRule("[aria-disabled=\"true\"]") {
        Modifier
            .disabledSurface()
            .styleModifier { property("transform", "none") }
    }
    /**
     * Working is not unavailable.
     *
     * The rule above is right for a control the reader cannot use and wrong for one that is doing
     * the thing they just asked for. `loading` sets `aria-disabled` to refuse the press, and the
     * wash then took the whole button — accent fill, label and spinner as one composited layer — to
     * `disabledSurface()`'s 0.56. On a filled primary that lifts `accent` to #9690F5 in Day, and a
     * spinner drawn over it cannot clear 3:1 at any colour, because element opacity moves the ring
     * and the ground it is on toward the surface together. The measured result on `/ds` was a solid
     * pale slab with nothing legible on it — the one state the receipt-after-estimate loop depends
     * on being visible was the faintest thing on the card.
     *
     * So the paint comes back for busy-and-available only. `aria-busy` is written on every button,
     * and `data-disabled="false"` is what separates this from a button that is genuinely
     * unavailable *and* loading; the editor's explained refusal, which sets `aria-disabled` by hand
     * with `loading = false`, keeps its wash untouched.
     */
    cssRule("[aria-busy=\"true\"][data-disabled=\"false\"]") {
        Modifier.opacity(1)
    }
    /**
     * A filled button that has already been repainted for unavailability must not also be faded.
     *
     * `LButtonVariant.colors` swaps PRIMARY and DANGER to the neutral disabled pair when they are
     * unavailable, and that pair is chosen to clear 4.5:1 as drawn. Washing it to 0.56 on top would
     * take it back to 2.11:1 — worse than the accent fill this replaces — so the two mechanisms are
     * mutually exclusive by construction rather than by hoping nobody stacks them. `cursor:
     * not-allowed` from `disabledSurface()` is left in place; it is the half of that helper that
     * says something true about a control the reader cannot press.
     */
    cssRule("[data-disabled=\"true\"][data-neutral-disabled=\"true\"]") {
        Modifier.opacity(1)
    }
    /**
     * Working must not resize the button.
     *
     * The spinner used to render *in place of* `leadingIcon` — which is a swap for a button that has
     * an icon and an addition for one that does not. The editor's Save button does not: it is
     * `LButtonSize.SM` with no icon, so every ⌘S grew it by an icon plus the 8px gap, and because
     * the metadata beside it is `flex: 1 1 auto` the whole action bar slid sideways and back on the
     * single most frequent action in the product. Nothing about that was animated, which made it a
     * snap rather than a slide, and a layout property is the one thing this design system will not
     * animate to smooth over.
     *
     * So the spinner comes out of flow and sits over the middle of the button, and the label is
     * turned transparent rather than removed — the text keeps its box, so the width is identical
     * before, during and after.
     */
    cssRule("[data-busy-label=\"hidden\"]") {
        Modifier.color(Color.rgba(0, 0, 0, 0f))
    }
    /**
     * The spinner is not the label, so blanking the label must not blank it.
     *
     * [LSpinner] now draws its ring from `currentColor`, which is how it reads on a filled button
     * instead of against it — and `currentColor` on this button is the transparent the rule above
     * just set. This puts the foreground back for the one child that is not the text. The label is
     * a bare text node with no element of its own, which is why the blanking stays on the root and
     * this is an exception rather than the other way round.
     */
    cssRule("[data-busy-label=\"hidden\"] .l-spinner") {
        Modifier.color(LButtonForegroundVar.value())
    }
}

val LButtonIconStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "inline-flex")
            property("flex", "0 0 auto")
        }
    }
    cssRule(" > svg") {
        Modifier
            .width(100.percent)
            .height(100.percent)
    }
}

@Composable
fun LButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    variant: LButtonVariant = LButtonVariant.PRIMARY,
    size: LButtonSize = LButtonSize.MD,
    enabled: Boolean = true,
    leadingIcon: (@Composable () -> Unit)? = null,
    loading: Boolean = false,
    /**
     * Overrides what assistive technology announces, when the visible label is not enough on its own.
     *
     * For a list where every row's button reads "Open", the visible text is right — the column
     * header and the row supply the context a sighted reader needs — but a screen reader hears
     * "Open" eight times with nothing to tell them apart. This adds the missing noun without
     * cluttering the button.
     */
    accessibleLabel: String? = null,
    /**
     * Draws the focus ring for a dark band rather than for paper.
     *
     * An ink band's ground is the page palette's `textPrimary`, so a control sitting on one must
     * take its contrast from [onInkPalette] — the palette defined for exactly that ground — and not
     * from the page's. Set it on any button placed inside a `BandGround.Ink` section; leaving it
     * false is right everywhere else.
     */
    onInk: Boolean = false,
    /**
     * A glyph after the label, for a button whose action continues somewhere rather than starting
     * here.
     *
     * Last in the list, and defaulted, so that every call site written before it keeps compiling
     * whatever order it passed its arguments in.
     *
     * A chevron on a split button's trigger, a caret on a disclosure, an arrow on "Continue" — the
     * glyph says what happens *after* the press, which is the one thing a leading icon cannot say
     * because a reader meets it before the verb. Sized and slotted exactly like [leadingIcon], so a
     * button carrying both stays symmetrical.
     */
    trailingIcon: (@Composable () -> Unit)? = null,
) {
    Button(
        lButtonSurface(variant, size, onInk, enabled)
            .then(modifier)
            .toAttrs {
                type(ButtonType.Button)
                // Marks the button for the 40px hit-area strip in LButtonStyle. Only where the
                // rendered height is under the floor: MD is exactly 40 and LG is 48, so tagging
                // those would add a pseudo-element that covers nothing.
                if (size.height < HIT_AREA_FLOOR_PX) attr("data-hit-extend", "")
                if (accessibleLabel != null) attr("aria-label", accessibleLabel)
                attr("aria-busy", loading.toString())
                // Only the blank-and-centre path hides its text; see the spinner below.
                if (loading && leadingIcon == null && trailingIcon == null) {
                    attr("data-busy-label", "hidden")
                }
                // `loading` stays out of `disabled()`: a disabled element leaves the tab order and
                // blurs focus, which is exactly wrong for the control the save loop is driven from.
                // `aria-disabled` communicates the same unavailability without doing that.
                attr("data-disabled", (!enabled).toString())
                if (!enabled) disabled() else if (loading) attr("aria-disabled", "true")
                onClick { if (enabled && !loading) onClick() }
            },
    ) {
        LButtonBody(label, size, loading, leadingIcon, trailingIcon)
    }
}

/**
 * The button's ground, sizing and focus ring, so a link-shaped action cannot drift from a
 * button-shaped one.
 *
 * Every value here is a decision made once — the variant's four colours, the focus ring's palette
 * on ink versus paper, the height and padding a size means. Duplicating them for [LLinkButton]
 * would mean two places to change a token and one of them getting missed.
 */
@Composable
private fun lButtonSurface(
    variant: LButtonVariant,
    size: LButtonSize,
    onInk: Boolean,
    enabled: Boolean = true,
): Modifier {
    val palette = ColorMode.current.toSitePalette()
    val colors = variant.colors(palette, ColorMode.current, enabled)
    return LButtonStyle.toModifier()
        .thenIf(!enabled && variant.isFilled) { Modifier.attr("data-neutral-disabled", "true") }
        .setVariable(LButtonBackgroundVar, colors.background)
        .setVariable(LButtonHoverBackgroundVar, colors.hoverBackground)
        .setVariable(LButtonForegroundVar, colors.foreground)
        .setVariable(LButtonBorderVar, colors.border)
        .setVariable(
            LButtonFocusRingVar,
            if (onInk) ColorMode.current.onInkPalette().accent else palette.accent,
        )
        .height(size.height.px)
        .minimumControlHitArea()
        .padding(leftRight = size.horizontalPadding.px)
        .fontSize(size.fontSize)
}

/** The spinner, the icon slots and the label — identical whether the control is a button or a link. */
@Composable
private fun LButtonBody(
    label: String,
    size: LButtonSize,
    loading: Boolean,
    leadingIcon: (@Composable () -> Unit)?,
    trailingIcon: (@Composable () -> Unit)?,
) {
    // Which slot the spinner takes when the button goes busy. Leading if there is one, else the
    // trailing one — a button with any glyph at all has somewhere to put the spinner that does
    // not cost the reader the label.
    val spinnerSlot = when {
        leadingIcon != null -> LButtonIconSlot.LEADING
        trailingIcon != null -> LButtonIconSlot.TRAILING
        else -> null
    }

    // Out of flow and centred, so the label below keeps its box and the button keeps its width.
    // See the `[data-busy-label]` rule in [LButtonStyle] for why that matters.
    //
    // Only when there is no icon to displace. A button carrying one has a better answer — the
    // spinner takes the icon's slot and the label stays readable — and blanking those too would
    // trade "Export, with a spinner where the icon was" for a button with nothing on it, on ten
    // call sites that never had the defect this centring exists to fix.
    if (loading && spinnerSlot == null) {
        LSpinner(
            modifier = Modifier
                .position(Position.Absolute)
                .iconSize(size.iconSize)
                .styleModifier {
                    property("left", "50%")
                    property("top", "50%")
                    // Negative margins rather than `translate(-50%, -50%)`: the spinner already
                    // owns a rotation of its own, and one element with two reasons to write a
                    // transform is one too many.
                    //
                    // `calc` rather than arithmetic on the token, because the token is now a
                    // `CSSSizeValue` — a length with a unit, which is the point of moving it
                    // onto [IconSize] — and halving it belongs to CSS.
                    property("margin-left", "calc(-1 * ${size.iconSize} / 2)")
                    property("margin-top", "calc(-1 * ${size.iconSize} / 2)")
                },
            size = LSpinnerSize.SM,
            label = "",
        )
    }
    if (leadingIcon != null) {
        LButtonIconSlotBox(size, LButtonIconSlot.LEADING) {
            // The slot is the same size either way, so swapping the icon for a spinner moves
            // nothing. This is the path most buttons in the product take, and it is why the
            // label beside it is left readable.
            if (loading) LSpinner(size = LSpinnerSize.SM, label = "") else leadingIcon()
        }
    }
    Text(label)
    if (trailingIcon != null) {
        LButtonIconSlotBox(size, LButtonIconSlot.TRAILING) {
            if (loading && spinnerSlot == LButtonIconSlot.TRAILING) {
                LSpinner(size = LSpinnerSize.SM, label = "")
            } else {
                trailingIcon()
            }
        }
    }
}

/**
 * Which side of the label a glyph sits on.
 *
 * Written into the DOM as `data-icon-slot` rather than left implicit in document order: a test that
 * wants to know the trailing icon rendered should not have to count children and hope the label is
 * a text node between them.
 */
private enum class LButtonIconSlot(val attributeValue: String) {
    LEADING("leading"),
    TRAILING("trailing"),
}

/** One square glyph box, sized off the button rather than off the glyph. */
@Composable
private fun LButtonIconSlotBox(
    size: LButtonSize,
    slot: LButtonIconSlot,
    content: @Composable () -> Unit,
) {
    Span(
        LButtonIconStyle.toModifier()
            .iconSize(size.iconSize)
            .toAttrs { attr("data-icon-slot", slot.attributeValue) },
    ) {
        content()
    }
}

/**
 * A button that is a link, for an action whose whole job is going somewhere.
 *
 * `LButton(onClick = { router.navigateTo(path) })` renders a `<button>`, and a `<button>` cannot be
 * opened in a new tab, copied as an address, or told apart from an action that changes something.
 * On a work queue that is the wrong promise: "Continue" opens a list, and a translator who wants it
 * beside what they already have should be able to middle-click it like every other row on the page.
 *
 * No `loading` and no `enabled`. A link either goes somewhere or should not be rendered as one — a
 * disabled anchor is a control that looks pressable, takes focus, and does nothing, and the honest
 * form of "not yet" is [LButton] with `enabled = false`.
 */
@Composable
fun LLinkButton(
    label: String,
    path: String,
    modifier: Modifier = Modifier,
    variant: LButtonVariant = LButtonVariant.PRIMARY,
    size: LButtonSize = LButtonSize.MD,
    leadingIcon: (@Composable () -> Unit)? = null,
    accessibleLabel: String? = null,
    onInk: Boolean = false,
    /**
     * A glyph after the label — the same slot [LButton] draws, so a link-shaped action and a
     * button-shaped one cannot look different. Last and defaulted for the same reason it is there.
     */
    trailingIcon: (@Composable () -> Unit)? = null,
) {
    Link(
        path = path,
        // Silk paints links; this one is already painted by the button surface, and letting both
        // through gives an underlined, accent-coloured label inside a filled button.
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        modifier = lButtonSurface(variant, size, onInk)
            .then(modifier)
            .thenIf(size.height < HIT_AREA_FLOOR_PX) { Modifier.attr("data-hit-extend", "") }
            .thenIf(accessibleLabel != null) { Modifier.attr("aria-label", accessibleLabel!!) },
    ) {
        LButtonBody(label, size, loading = false, leadingIcon, trailingIcon)
    }
}

/** Grows an icon button's clickable area to the 40px minimum without resizing the button. */
val LIconButtonHitAreaStyle = CssStyle {
    base { Modifier.position(Position.Relative) }
    cssRule("::after") {
        Modifier
            .content("")
            .position(Position.Absolute)
            .styleModifier {
                // Centres a 40x40 area on the button whatever its rendered size.
                property("top", "50%")
                property("left", "50%")
                property("width", "${HIT_AREA_FLOOR_PX}px")
                property("height", "${HIT_AREA_FLOOR_PX}px")
                property("transform", "translate(-50%, -50%)")
            }
    }
}

@Composable
fun LIconButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    variant: LButtonVariant = LButtonVariant.GHOST,
    size: LButtonSize = LButtonSize.MD,
    enabled: Boolean = true,
    loading: Boolean = false,
    /**
     * Draws the focus ring for a dark band rather than for paper.
     *
     * An ink band's ground is the page palette's `textPrimary`, so a control sitting on one must
     * take its contrast from [onInkPalette] — the palette defined for exactly that ground — and not
     * from the page's. Set it on any button placed inside a `BandGround.Ink` section; leaving it
     * false is right everywhere else.
     */
    onInk: Boolean = false,
    /**
     * Writes the browser's own `title` tooltip beside the accessible name.
     *
     * On by default, because a bare icon button standing on its own has nothing else to explain it
     * to a pointer. Set it false wherever a real tooltip is already attached — [LIconAction] does —
     * or the reader gets two explanations of one control: the styled one this product positioned,
     * and the operating system's, arriving later, somewhere else, in a different typeface, on top.
     */
    nativeTitle: Boolean = true,
    content: @Composable () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val colors = variant.colors(palette, ColorMode.current, enabled)
    Button(
        LButtonStyle.toModifier()
            .thenIf(!enabled && variant.isFilled) { Modifier.attr("data-neutral-disabled", "true") }
            .setVariable(LButtonBackgroundVar, colors.background)
            .setVariable(LButtonHoverBackgroundVar, colors.hoverBackground)
            .setVariable(LButtonForegroundVar, colors.foreground)
            .setVariable(LButtonBorderVar, colors.border)
            .setVariable(
                LButtonFocusRingVar,
                if (onInk) ColorMode.current.onInkPalette().accent else palette.accent,
            )
            .width(size.height.px)
            .height(size.height.px)
            .padding(0.px)
            .justifyContent(JustifyContent.Center)
            // A 32px icon button is a comfortable *visual* size and an uncomfortable target. The
            // pseudo-element grows the area that responds to a click out to 40px without changing
            // the button's own box, so surrounding layout is untouched and neighbouring targets do
            // not start overlapping.
            .then(LIconButtonHitAreaStyle.toModifier())
            .then(modifier)
            .toAttrs {
                type(ButtonType.Button)
                attr("aria-label", label)
                // "This control is a glyph and nothing else." `PageHeaderStyle` reads it to decide
                // whether a header's actions slot deserves a row of its own below MD; see the
                // ICON_ONLY_ACTIONS selector there. Written by the component rather than by the
                // fifteen call sites, because it is a fact about the component.
                attr("data-icon-action", "true")
                if (nativeTitle) attr("title", label)
                attr("aria-busy", loading.toString())
                // See LButton above: `loading` must not disable the element, or focus drops to
                // <body> on every press.
                attr("data-disabled", (!enabled).toString())
                if (!enabled) disabled() else if (loading) attr("aria-disabled", "true")
                onClick { if (enabled && !loading) onClick() }
            },
    ) {
        if (loading) {
            LSpinner(
                modifier = Modifier.iconSize(size.iconSize),
                size = LSpinnerSize.SM,
                label = "",
            )
        } else {
            Span(
                LButtonIconStyle.toModifier()
                    .iconSize(size.iconSize)
                    .toAttrs(),
            ) {
                content()
            }
        }
    }
}

private data class LButtonColors(
    val background: Color,
    val hoverBackground: Color,
    val foreground: Color,
    val border: Color,
)

private fun LButtonVariant.colors(
    palette: SitePalette,
    colorMode: ColorMode,
    enabled: Boolean = true,
): LButtonColors = when {
    // **A disabled filled button is not a faded filled button.**
    //
    // `disabledSurface()` composites the whole control at 0.56, which on PRIMARY lifted `accent`
    // to #9690F5 in Day and took its white label with it: the measured label-on-fill ratio on `/ds`
    // was 2.84:1, against a 4.5:1 floor. Fading a filled control cannot do better than that, because
    // opacity moves the label and the ground it sits on toward the surface together — the same
    // argument the `aria-busy` rule in [LButtonStyle] already makes for a spinner.
    //
    // So a filled variant stops being filled when it is unavailable, and drops to the neutral pair
    // the rest of the design system already uses for a recessed surface: `surfaceSunken` under
    // `textMuted` (4.73:1 in Day, 6.52:1 in Night) inside `borderStrong`. [LButtonStyle] cancels the
    // opacity for exactly these buttons, so the ratio measured here is the ratio a reader gets.
    // GHOST and SECONDARY keep the wash: neither carries a fill, so neither has the problem.
    !enabled && isFilled -> LButtonColors(
        background = palette.surfaceSunken,
        hoverBackground = palette.surfaceSunken,
        foreground = palette.textMuted,
        border = palette.borderStrong,
    )

    else -> colorsWhenAvailable(palette, colorMode)
}

/**
 * Whether the variant's resting state is a solid ground the label is drawn *on*.
 *
 * The two that are decide unavailability differently from the two that are not; see [colors].
 */
private val LButtonVariant.isFilled: Boolean
    get() = this == LButtonVariant.PRIMARY || this == LButtonVariant.DANGER

private fun LButtonVariant.colorsWhenAvailable(
    palette: SitePalette,
    colorMode: ColorMode,
): LButtonColors = when (this) {
    LButtonVariant.PRIMARY -> LButtonColors(
        background = palette.accent,
        hoverBackground = palette.accentHover,
        foreground = palette.accentContrast,
        border = palette.accent,
    )
    LButtonVariant.SECONDARY -> LButtonColors(
        background = palette.surface,
        hoverBackground = palette.surfaceRaised,
        foreground = palette.textPrimary,
        border = palette.borderStrong,
    )
    LButtonVariant.GHOST -> LButtonColors(
        background = Color.rgba(0, 0, 0, 0f),
        hoverBackground = palette.surfaceRaised,
        foreground = palette.textSecondary,
        border = Color.rgba(0, 0, 0, 0f),
    )
    LButtonVariant.DANGER -> LButtonColors(
        background = palette.danger,
        hoverBackground = palette.danger.shifted(colorMode),
        // accentContrast, not white. Night's danger is a light coral and white on it is 2.78:1;
        // this token inverts by mode for exactly that reason, giving 5.76 in Day and 6.74 in Night.
        foreground = palette.accentContrast,
        border = palette.danger,
    )
}
